import { useEffect, useRef } from 'react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { MessageSquare, Mail, Phone, ExternalLink, Loader2 } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { MessageBubble } from './MessageBubble';
import { MessageComposer } from './MessageComposer';
import { useThreadMessages, useMarkThreadRead } from '@/hooks/useOmniChannel';
import { getInitials } from './initials';
import type { ConversationThread, ThreadMessage } from '@/types/omniChannel';

interface ConversationViewProps {
  thread: ConversationThread | null;
}

// Group messages by date
function groupMessagesByDate(messages: ThreadMessage[]): Map<string, ThreadMessage[]> {
  const groups = new Map<string, ThreadMessage[]>();

  for (const message of messages) {
    const dateKey = format(new Date(message.timestamp), 'yyyy-MM-dd');
    if (!groups.has(dateKey)) {
      groups.set(dateKey, []);
    }
    groups.get(dateKey)!.push(message);
  }

  return groups;
}

function formatDateHeader(dateKey: string): string {
  const date = new Date(dateKey);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (dateKey === format(today, 'yyyy-MM-dd')) {
    return 'Today';
  } else if (dateKey === format(yesterday, 'yyyy-MM-dd')) {
    return 'Yesterday';
  } else {
    return format(date, 'EEEE, d MMMM yyyy');
  }
}

export function ConversationView({ thread }: ConversationViewProps) {
  const navigate = useNavigate();
  const scrollRef = useRef<HTMLDivElement>(null);
  const markAsRead = useMarkThreadRead();
  const { data: messages, isLoading } = useThreadMessages(thread?.id || null);

  // Mark thread as read when selected
  useEffect(() => {
    if (thread?.id) {
      markAsRead(thread.id);
    }
  }, [thread?.id, markAsRead]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  if (!thread) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground bg-background">
        <MessageSquare className="h-12 w-12 mb-4 opacity-50" />
        <p className="text-lg font-medium">Select a conversation</p>
        <p className="text-sm">Choose a thread from the list to view messages</p>
      </div>
    );
  }

  const messageGroups = messages ? groupMessagesByDate(messages) : new Map();

  // Determine what communication types are available
  const hasPhone = !!thread.contact_phone;
  const hasEmail = !!thread.contact_email;

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Header */}
      <div className="border-b border-border bg-card p-4">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
            {getInitials(thread.contact_name)}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h3 className="truncate text-sm font-semibold text-foreground">
                {thread.contact_name}
              </h3>
              {thread.campaign_name && (
                <Badge variant="secondary" className="hidden shrink-0 lg:inline-flex">
                  {thread.campaign_name}
                </Badge>
              )}
            </div>
            <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
              {hasPhone && <span>{thread.contact_phone}</span>}
              {hasEmail && (
                <>
                  {hasPhone && <span className="text-muted-foreground/50">·</span>}
                  <span className="max-w-[150px] truncate">{thread.contact_email}</span>
                </>
              )}
              <span className="text-muted-foreground/50">·</span>
              <span className="inline-flex items-center gap-1">
                {thread.last_message_type === 'sms' ? (
                  <MessageSquare className="h-3 w-3" />
                ) : (
                  <Mail className="h-3 w-3" />
                )}
                {thread.last_message_type === 'sms' ? 'SMS' : 'Email'}
              </span>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {hasPhone && (
              <Button variant="outline" size="sm" className="h-8" asChild>
                <a href={`tel:${thread.contact_phone}`}>
                  <Phone className="h-3.5 w-3.5 mr-1" />
                  Call
                </a>
              </Button>
            )}
            {thread.lead_id && (
              <Button
                variant="outline"
                size="sm"
                className="h-8"
                onClick={() => navigate(`/leads/${thread.lead_id}`)}
              >
                <ExternalLink className="h-3.5 w-3.5 mr-1" />
                Open lead
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1 bg-background p-4" ref={scrollRef}>
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : messages?.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <p>No messages yet</p>
          </div>
        ) : (
          <div className="space-y-6">
            {Array.from(messageGroups.entries()).map(([dateKey, dateMessages]) => (
              <div key={dateKey}>
                {/* Date separator */}
                <div className="flex items-center gap-4 my-4">
                  <Separator className="flex-1" />
                  <span className="text-[11px] font-medium text-muted-foreground">
                    {formatDateHeader(dateKey)}
                  </span>
                  <Separator className="flex-1" />
                </div>

                {/* Messages for this date */}
                <div>
                  {dateMessages.map((message) => (
                    <MessageBubble key={message.id} message={message} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </ScrollArea>

      {/* Composer */}
      <div className="p-4 border-t border-border bg-card">
        <MessageComposer
          to={thread.contact_phone}
          toEmail={thread.contact_email}
          leadId={thread.lead_id}
          customerId={thread.customer_id}
          trackingNumber={thread.tracking_number}
          defaultMode={thread.last_message_type}
        />
      </div>
    </div>
  );
}
